import { motion, AnimatePresence } from "framer-motion";
import { useLanguage, Language } from "@/contexts/LanguageContext";
import { Button } from "@/components/ui/button";
import { Globe } from "lucide-react";

const languages: { code: Language; name: string; flag: string }[] = [
  { code: "en", name: "English", flag: "EN" },
  { code: "fr", name: "Français", flag: "FR" },
  { code: "es", name: "Español", flag: "ES" },
  { code: "de", name: "Deutsch", flag: "DE" },
  { code: "it", name: "Italiano", flag: "IT" },
  { code: "zh", name: "中文", flag: "中" },
  { code: "ja", name: "日本語", flag: "日" },
  { code: "ar", name: "العربية", flag: "ع" },
];

export function LanguageSelectorPopup() {
  const { showLanguageSelector, setLanguage, t } = useLanguage();

  if (!showLanguageSelector) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm"
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          className="bg-card rounded-3xl p-8 shadow-2xl max-w-md w-full mx-4 border"
        >
          <div className="text-center mb-8">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-coral-light flex items-center justify-center">
              <Globe className="w-8 h-8 text-coral" />
            </div>
            <h2 className="font-display text-2xl font-bold mb-2">
              {t("lang.title")}
            </h2>
            <p className="text-muted-foreground">
              {t("lang.subtitle")}
            </p>
          </div>

          <div className="space-y-3">
            {languages.map((lang) => (
              <button
                key={lang.code}
                onClick={() => setLanguage(lang.code)}
                className="w-full p-4 rounded-2xl border-2 border-muted hover:border-coral transition-all flex items-center gap-4 bg-background hover:bg-coral-light/30"
              >
                <span className="text-lg font-bold">{lang.flag}</span>
                <span className="font-semibold text-lg">{lang.name}</span>
              </button>
            ))}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

export function LanguageSwitcher() {
  const { language, setLanguage } = useLanguage();

  const currentLang = languages.find((l) => l.code === language);

  return (
    <div className="relative group">
      <Button
        variant="outline"
        size="sm"
        className="flex items-center gap-2 bg-background/80 backdrop-blur-sm px-3"
      >
        <span className="text-base font-bold leading-none">{currentLang?.flag}</span>
      </Button>
      
      <div className="absolute right-0 mt-2 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
        <div className="bg-card rounded-xl shadow-lg border p-2 min-w-[60px]">
          {languages.map((lang) => (
            <button
              key={lang.code}
              onClick={() => setLanguage(lang.code)}
              className={`w-full p-2 rounded-lg flex items-center justify-center hover:bg-muted transition-colors ${
                language === lang.code ? "bg-coral-light" : ""
              }`}
              title={lang.name}
            >
              <span className="text-sm font-bold leading-none">{lang.flag}</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
